import React, { useState, useEffect } from 'react';
import { API_BASE } from '../utils/api';
import { authHeaders, isAuthenticated } from '../utils/auth';

function RouteDiscussion({ route, onClose }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [username, setUsername] = useState(localStorage.getItem('username') || '');
  const [submitting, setSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState(null);

  useEffect(() => {
    if (route) {
      fetchComments();
    }
    // eslint-disable-next-line
  }, [route]);

  const fetchComments = async () => {
    if (!route) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/routes/${route.id}/comments`);
      const data = await response.json();
      setComments(data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();

    if (!newComment.trim()) return;

    setSubmitting(true);

    try {
      // Save username preference
      if (username) {
        localStorage.setItem('username', username);
      }

      const response = await fetch(`${API_BASE}/routes/${route.id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders()
        },
        body: JSON.stringify({
          comment_text: newComment,
          username: username || 'Anonymous',
          parent_id: replyTo?.id || null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to post comment');
      }

      // Clear form and refresh comments
      setNewComment('');
      setReplyTo(null);
      await fetchComments();
    } catch (err) {
      console.error('Error posting comment:', err);
      alert('Failed to post comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const Comment = ({ comment, depth = 0 }) => {
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [upvoteCount, setUpvoteCount] = useState(comment.upvote_count || 0);
    const [hasUpvoted, setHasUpvoted] = useState(false);

    const handleUpvote = async () => {
      if (!isAuthenticated()) return;
      try {
        const resp = await fetch(`${API_BASE}/comments/${comment.id}/upvote`, {
          method: hasUpvoted ? 'DELETE' : 'POST',
          headers: authHeaders(),
        });
        if (resp.ok) {
          const data = await resp.json();
          setUpvoteCount(data.upvote_count);
          setHasUpvoted(!hasUpvoted);
        }
      } catch (err) {
        console.error('Upvote error:', err);
      }
    };

    return (
      <div className="comment" style={{ marginLeft: depth > 0 ? '2rem' : '0' }}>
        <div className="comment-header">
          <span className="comment-author">{comment.username || 'Anonymous'}</span>
          <span className="comment-time">
            {new Date(comment.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </span>
        </div>
        <div className="comment-body">{comment.comment_text}</div>
        <div className="comment-actions">
          <button
            className={`upvote-btn ${hasUpvoted ? 'active' : ''}`}
            onClick={handleUpvote}
            disabled={!isAuthenticated()}
            title={isAuthenticated() ? 'Upvote this comment' : 'Sign in to upvote'}
          >
            ▲ {upvoteCount}
          </button>
          <button
            className="reply-btn"
            onClick={() => {
              setReplyTo(comment);
              setShowReplyForm(!showReplyForm);
            }}
          >
            Reply
          </button>
        </div>
        
        {showReplyForm && replyTo?.id === comment.id && (
          <div className="reply-form-inline">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a reply..."
              rows="3"
            />
            <div className="reply-form-actions">
              <button 
                className="btn btn-primary"
                onClick={handleSubmitComment}
                disabled={submitting || !newComment.trim()}
              >
                Reply
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowReplyForm(false);
                  setReplyTo(null);
                  setNewComment('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        
        {comment.replies && comment.replies.length > 0 && (
          <div className="comment-replies">
            {comment.replies.map(reply => (
              <Comment key={reply.id} comment={reply} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  };

  if (!route) return null;

  return (
    <div className="discussion-overlay" onClick={onClose}>
      <div className="discussion-panel" onClick={(e) => e.stopPropagation()}>
        <div className="discussion-header">
          <div>
            <h2>Route Discussion</h2>
            <div className="route-display">
              {route.origin} → {route.destination}
              <span className="route-cities">
                {route.origin_airport?.city} to {route.destination_airport?.city}
              </span>
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="discussion-content">
          {/* New Comment Form */}
          <div className="new-comment-form">
            <h3>Add a Comment</h3>
            <form onSubmit={handleSubmitComment}>
              <input
                type="text"
                placeholder="Your name (optional)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="username-input"
              />
              <textarea
                placeholder="Share your thoughts about this route..."
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                rows="4"
                disabled={submitting || replyTo}
              />
              {replyTo && (
                <div className="replying-to">
                  Replying to {replyTo.username}'s comment
                  <button 
                    type="button"
                    onClick={() => {
                      setReplyTo(null);
                      setNewComment('');
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={submitting || !newComment.trim()}
              >
                {submitting ? 'Posting...' : 'Post Comment'}
              </button>
            </form>
          </div>

          {/* Comments List */}
          <div className="comments-section">
            <h3>
              Comments ({comments.length})
            </h3>
            
            {loading ? (
              <div className="loading-comments">Loading comments...</div>
            ) : comments.length === 0 ? (
              <div className="no-comments">
                No comments yet. Be the first to share your thoughts!
              </div>
            ) : (
              <div className="comments-list">
                {comments.map(comment => (
                  <Comment key={comment.id} comment={comment} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RouteDiscussion;
